import styled from 'styled-components';
import theme from 'theme/theme';
import media from 'utils/MediaQueries';

const Form = styled.form`
	width: 100%;
	max-width: 820px;
`;

const Fieldset = styled.fieldset`
	border: none;
	margin: 0 0 34px 0;
	padding: 0;

	&:last-of-type {
		margin-bottom: 26px;
	}
`;

const Legend = styled.legend`
	display: block;
	width: 100%;
	font-family: ${theme.monoFont};
	color: ${theme.primary1};
	font-size: 11.5px;
	font-weight: 500;
	letter-spacing: 2px;
	text-transform: uppercase;
	padding: 0 0 14px 0;
	margin-bottom: 20px;
	border-bottom: 1px solid ${theme.rule};
`;

/**
 * Two columns on desktop so the short answers (name, role, size bands) sit
 * side by side instead of running down the page as a long ladder. Anything
 * that needs the full width opts out with $wide.
 */
const FieldGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 18px 20px;

	${media.tablet`
		grid-template-columns: 1fr;
	`};
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	grid-column: ${(props) => (props.$wide ? '1 / -1' : 'auto')};
`;

const Label = styled.label`
	display: block;
	font-size: 13.5px;
	color: ${theme.chalk};
	opacity: 0.72;
	margin-bottom: 8px;
`;

const Required = styled.span`
	color: ${theme.primary1};
	margin-left: 3px;
`;

/**
 * One control surface shared by input, select and textarea, so a row of mixed
 * field types still reads as a single line of boxes.
 */
const controlStyles = `
	width: 100%;
	box-sizing: border-box;
	background-color: rgba(255, 255, 255, 0.035);
	border: 1px solid ${theme.rule};
	border-radius: 6px;
	color: ${theme.chalk};
	font-family: ${theme.primaryFont};
	font-size: 15px;
	padding: 12px 14px;
	outline: none;
	transition: border-color 0.15s ease;

	&::placeholder {
		color: ${theme.chalk};
		opacity: 0.3;
	}

	&:focus {
		border-color: ${theme.primary1};
	}
`;

const Input = styled.input`
	${controlStyles};
	border-color: ${(props) => (props.$error ? theme.danger : theme.rule)};
`;

const Textarea = styled.textarea`
	${controlStyles};
	min-height: 110px;
	resize: vertical;
	line-height: 1.6;
`;

const Select = styled.select`
	${controlStyles};
	height: 46px;
	cursor: pointer;
	border-color: ${(props) => (props.$error ? theme.danger : theme.rule)};
	/* Native arrow is unstyleable and light-on-dark reads badly, so draw one. */
	appearance: none;
	-webkit-appearance: none;
	background-image: linear-gradient(
			45deg,
			transparent 50%,
			${theme.chalk} 50%
		),
		linear-gradient(135deg, ${theme.chalk} 50%, transparent 50%);
	background-position: calc(100% - 19px) 21px, calc(100% - 14px) 21px;
	background-size: 5px 5px, 5px 5px;
	background-repeat: no-repeat;

	option {
		background-color: ${theme.background3};
		color: ${theme.chalk};
	}
`;

const ErrorText = styled.p`
	margin: 7px 0 0 0;
	font-family: ${theme.monoFont};
	font-size: 11.5px;
	color: ${theme.danger};
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 22px;
	flex-wrap: wrap;
	padding-top: 8px;
`;

const Note = styled.p`
	margin: 22px 0 0 0;
	font-family: ${theme.monoFont};
	font-size: 12px;
	line-height: 1.7;
	color: ${theme.chalk};
	opacity: 0.45;
	max-width: 560px;
`;

const Sent = styled.div`
	max-width: 620px;
	border: 1px solid ${theme.rule};
	border-left: 3px solid ${theme.primary1};
	border-radius: 6px;
	padding: 28px 26px 26px 23px;
`;

const SentTitle = styled.h3`
	font-family: ${theme.headerFont};
	color: ${theme.chalk};
	font-size: 22px;
	margin: 0 0 12px 0;
`;

const SentText = styled.p`
	margin: 0 0 10px 0;
	font-size: 15px;
	line-height: 1.7;
	color: ${theme.chalk};
	opacity: 0.72;

	a {
		color: ${theme.primary1};
	}

	&:last-child {
		margin-bottom: 0;
	}
`;

export {
	Form,
	Fieldset,
	Legend,
	FieldGrid,
	Field,
	Label,
	Required,
	Input,
	Textarea,
	Select,
	ErrorText,
	Actions,
	Note,
	Sent,
	SentTitle,
	SentText,
};
